import { useState, useRef, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";

export interface ImageHoverZoomProps {
  src: string;
  alt: string;
  className?: string;
}

export default function ImageHoverZoom({
  src,
  alt,
  className = "",
}: ImageHoverZoomProps) {
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [hovering, setHovering] = useState(true);
  const [mouse, setMouse] = useState({ x: 0.5, y: 0.5 });
  const [containerRect, setContainerRect] = useState<DOMRect | null>(null);
  const [imgSize, setImgSize] = useState<{ w: number; h: number } | null>(null);

  const lensRatio = 0.35;
  const zoomPanelSize = 480;

  const updateRect = useCallback(() => {
    if (imageContainerRef.current) {
      setContainerRect(imageContainerRef.current.getBoundingClientRect());
    }
  }, []);

  const onImageLoad = useCallback(() => {
    const img = imgRef.current;
    if (img && img.naturalWidth > 0 && img.naturalHeight > 0) {
      setImgSize({ w: img.naturalWidth, h: img.naturalHeight });
    }
  }, []);

  useEffect(() => {
    const img = imgRef.current;
    if (img?.complete && img.naturalWidth > 0) {
      setImgSize({ w: img.naturalWidth, h: img.naturalHeight });
    }
  }, [src]);

  useEffect(() => {
    updateRect();
    window.addEventListener("resize", updateRect);
    window.addEventListener("scroll", updateRect, true);
    return () => {
      window.removeEventListener("resize", updateRect);
      window.removeEventListener("scroll", updateRect, true);
    };
  }, [updateRect]);

  const onMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const el = imageContainerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setContainerRect(r);
    const x = (e.clientX - r.left) / r.width;
    const y = (e.clientY - r.top) / r.height;
    setMouse({
      x: Math.max(0, Math.min(1, x)),
      y: Math.max(0, Math.min(1, y)),
    });
  }, []);

  const onEnter = useCallback(() => {
    updateRect();
    setHovering(true);
  }, [updateRect]);

  const onLeave = useCallback(() => setHovering(false), []);

  const W = containerRect?.width ?? 0;
  const H = containerRect?.height ?? 0;
  const lensPx = Math.min(Math.max(60, Math.min(W, H) * lensRatio), 180);
  const half = lensPx / 2;
  const lensLeft = Math.max(0, Math.min(W - lensPx, mouse.x * W - half));
  const lensTop = Math.max(0, Math.min(H - lensPx, mouse.y * H - half));

  const panelLeft = (containerRect?.right ?? 0) + 16;
  const panelTop = containerRect?.top ?? 0;
  const fitsRight = panelLeft + zoomPanelSize <= window.innerWidth - 16;
  const styleLeft = fitsRight ? panelLeft : (containerRect?.left ?? 0) - zoomPanelSize - 16;

  let zoomImageStyle: React.CSSProperties = {};
  if (imgSize && imgSize.w > 0 && imgSize.h > 0 && W > 0 && H > 0) {
    const Iw = imgSize.w;
    const Ih = imgSize.h;
    const scaleCover = Math.max(W / Iw, H / Ih);
    const imageOffsetX = (W - Iw * scaleCover) / 2;
    const imageOffsetY = (H - Ih * scaleCover) / 2;

    const zoomScale = (zoomPanelSize * scaleCover) / lensPx;
    const cropX = (lensLeft - imageOffsetX) / scaleCover;
    const cropY = (lensTop - imageOffsetY) / scaleCover;

    zoomImageStyle = {
      position: "absolute",
      width: Iw * zoomScale,
      height: Ih * zoomScale,
      left: -cropX * zoomScale,
      top: -cropY * zoomScale,
      maxWidth: "none",
    };
  }

  const showZoom = hovering && containerRect && imgSize && imgSize.w > 0;

  return (
    <>
      <div className={`relative w-full ${className}`.trim()}>
        <div
          ref={imageContainerRef}
          className="relative w-full rounded-xl md:rounded-2xl bg-gray-100 aspect-[9/10] overflow-hidden cursor-crosshair"
          onMouseMove={onMouseMove}
          onMouseEnter={onEnter}
          onMouseLeave={onLeave}
        >
          <img
            ref={imgRef}
            src={src}
            alt={alt}
            className={`block h-full mx-auto object-contain select-none pointer-events-none`}
            draggable={false}
            onLoad={onImageLoad}
          />

          {hovering && (
            <div
              className="absolute pointer-events-none z-10 border-2 border-white/90 rounded-sm"
              style={{
                width: lensPx,
                height: lensPx,
                left: lensLeft,
                top: lensTop,
                boxShadow: "0 0 0 9999px rgba(0,0,0,0.2)",
                backgroundColor: "rgba(255,255,255,0.1)",
              }}
            />
          )}
        </div>
      </div>

      {showZoom &&
        createPortal(
          <div
            className="hidden lg:block overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl"
            style={{
              position: "fixed",
              left: styleLeft,
              top: panelTop,
              width: zoomPanelSize,
              height: zoomPanelSize,
              zIndex: 9999,
              pointerEvents: "none",
            }}
          >
            <img
              src={src}
              alt=""
              draggable={false}
              className="absolute select-none pointer-events-none block"
              style={zoomImageStyle}
            />
          </div>,
          document.body,
        )}
    </>
  );
}
